import type { CustomPlacement, MockupTemplate, Point, ScreenWarpMode } from '../types';
import { getCategoryAspect } from '../categoryAspect';
import { fetchImage, createCanvas } from './loadImage';
import { warpImageToQuad } from './warp';
import { warpImageToDuo } from './duoWarp';
import { getDuoInputAspect, getTemplateInputCount, normalizeFlexibleDisplay, isBaseCustomFrameTemplate } from '@motionpitch/template-contract';
import {renderBaseCustomFrame} from './baseCustomFrame';
import type {BaseCustomFrameSettings} from '../baseCustomFrame';
import {
  applyMask,
  getMaskFactor,
  sharpenPackedMotionMaskFactor,
  type MaskChannel,
} from './mask';
import type { StaticBackgroundComposition } from './staticBackground';
import type { StaticRenderCache } from './StaticRenderCache';
import { setDynamicZoomLayers } from './dynamicZoomBackground';
import { isCanvasMemoryScopeActive, releaseScopedCanvas } from './canvasMemory';

export interface LoadedMockupAssets {
  template: MockupTemplate;
  base: CanvasImageSource;
  mask: CanvasImageSource;
  maskChannel?: MaskChannel;
  mask2: CanvasImageSource | null;
  mask2Channel?: MaskChannel;
  mask3: CanvasImageSource | null;
  mask3Channel?: MaskChannel;
  overlay: CanvasImageSource;
  backgroundMask: CanvasImageSource | null;
  reflectionMask: CanvasImageSource | null;
  reflectionBlurMask: CanvasImageSource | null;
  /** B&W mask applied to the user's screen content: white = maximum blur, black = no blur. */
  screenBlurMask: CanvasImageSource | null;
}

export interface DiffuseAdjustments {
  hue: number;
  saturation: number;
  brightness: number;
  contrast: number;
  monochrome: boolean;
}

interface ImageSourceRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

interface ImageDestRect {
  dx: number;
  dy: number;
  dw: number;
  dh: number;
}

export interface BackgroundAdjustments {
  transparent: boolean;
  mode: 'original' | 'tint' | 'tritone';
  shadowColor: string;
  midColor: string;
  highlightColor: string;
}

export type DeviceShadowPreset = 'soft' | 'close' | 'floating' | 'long';

export interface DeviceShadowAdjustments {
  enabled: boolean;
  preset: DeviceShadowPreset;
  /** Direction in screen coordinates: 45 degrees points down and right. */
  angle: number;
  /** Multiplier for the preset opacity. 100 preserves the preset's original strength. */
  opacity: number;
  color: string;
}

export interface ReflectionAdjustments {
  enabled: boolean;
  opacity: number;
  blur: number;
}

export interface ScreenOverlayAdjustments {
  opacity: number;
  /** Screen blur strength, 0.1-100. 50 is the neutral default. */
  blur?: number;
}

async function loadAdditionalScreenMask(
  template: MockupTemplate,
  screen: 2 | 3,
  variant: 'preview' | 'export'
): Promise<CanvasImageSource | null> {
  const required = screen <= getTemplateInputCount(template);
  if (!required && normalizeFlexibleDisplay(template.flexibleDisplay)) return null;
  const fullUrl = screen === 2 ? template.mask2 : template.mask3;
  const previewUrl = screen === 2 ? template.editMask2 : template.editMask3;
  // Preview derivatives can be unavailable while the original mask is valid.
  // Export must always use the original; never silently omit a required screen.
  const urls = [...new Set(variant === 'preview' ? [previewUrl, fullUrl] : [fullUrl])];
  for (const url of urls) {
    if (!url) continue;
    try {
      return await fetchImage(url);
    } catch {
      // Try the original mask before reporting a required asset failure.
    }
  }
  if (required) {
    throw new Error(`Unable to load Screen ${screen} mask. Please retry or choose another template.`);
  }
  return null;
}

export async function loadMockupAssets(
  template: MockupTemplate,
  variant: 'preview' | 'export' = 'export'
): Promise<LoadedMockupAssets> {
  if(isBaseCustomFrameTemplate(template)){
    const empty=createCanvas(1,1);
    return {template,base:empty,mask:empty,overlay:empty,mask2:null,mask3:null,backgroundMask:null,reflectionMask:null,reflectionBlurMask:null,screenBlurMask:null};
  }
  const baseUrl = variant === 'preview' ? template.editBase : template.base;
  const maskUrl = variant === 'preview' ? template.editMask : template.mask;
  const overlayUrl = variant === 'preview' ? template.editOverlay : template.overlay;
  const backgroundMaskUrl = variant === 'preview' ? template.editBackgroundMask : template.backgroundMask;
  const reflectionMaskUrl = variant === 'preview' ? template.editReflectionMask : template.reflectionMask;
  const reflectionBlurMaskUrl =
    variant === 'preview' ? template.editReflectionBlurMask : template.reflectionBlurMask;
  const screenBlurMaskUrl =
    variant === 'preview'
      ? template.editScreenBlurMask || template.screenBlurMask
      : template.screenBlurMask;

  if (variant === 'preview' && (!baseUrl || !maskUrl || !overlayUrl)) {
    throw new Error('Preview assets are missing for this template.');
  }

  const [base, mask, mask2, mask3, overlay, backgroundMask, reflectionMask, reflectionBlurMask, screenBlurMask] = await Promise.all([
    fetchImage(baseUrl),
    fetchImage(maskUrl),
    loadAdditionalScreenMask(template, 2, variant),
    loadAdditionalScreenMask(template, 3, variant),
    fetchImage(overlayUrl),
    backgroundMaskUrl ? fetchImage(backgroundMaskUrl).catch(() => null) : Promise.resolve(null),
    reflectionMaskUrl ? fetchImage(reflectionMaskUrl).catch(() => null) : Promise.resolve(null),
    reflectionBlurMaskUrl ? fetchImage(reflectionBlurMaskUrl).catch(() => null) : Promise.resolve(null),
    screenBlurMaskUrl ? fetchImage(screenBlurMaskUrl).catch(() => null) : Promise.resolve(null)
  ]);

  return {
    template,
    base,
    mask,
    maskChannel: 'luminance',
    mask2,
    mask2Channel: 'luminance',
    mask3,
    mask3Channel: 'luminance',
    overlay,
    backgroundMask,
    reflectionMask,
    reflectionBlurMask,
    screenBlurMask
  };
}

export function renderMaster(
  mockup: LoadedMockupAssets,
  userImage: CanvasImageSource | null,
  options: {
    baseCustomFrame?: BaseCustomFrameSettings;
    gridX?: number;
    gridY?: number;
    diffuse?: DiffuseAdjustments;
    /**
     * Painted over the diffuse layer and under the screen. The Puzzle Matte uses
     * it so a recoloured region sits above the Color Control sliders: whatever
     * hue, saturation or brightness the viewer dialled in, the colour they
     * picked for that region is the last thing applied to it.
     */
    diffuseOverlay?: CanvasImageSource | null;
    background?: BackgroundAdjustments;
    staticBackground?: StaticBackgroundComposition & { source: CanvasImageSource };
    /** Opt-in foreground master, painted with drawDynamicZoomFrame and its background. */
    separateCameraBackground?: boolean;
    deviceShadow?: DeviceShadowAdjustments;
    reflection?: ReflectionAdjustments;
    screenOverlay?: ScreenOverlayAdjustments;
    useAlternateMask?: boolean;
    customPlacement?: CustomPlacement | null;
    userImage2?: CanvasImageSource | null;
    customPlacement2?: CustomPlacement | null;
    userImage3?: CanvasImageSource | null;
    customPlacement3?: CustomPlacement | null;
    warpMode?: ScreenWarpMode;
    warpMode2?: ScreenWarpMode;
    warpMode3?: ScreenWarpMode;
    renderSize?: number;
    screenRenderScale?: number;
    screenQuadOverride?: Point[];
    screenQuadOverride2?: Point[];
    screenQuadOverride3?: Point[];
    reflectionQuadOverride?: Point[];
    /** When true the screen blur mask is skipped for performance (realtime playback). */
    realtime?: boolean;
    /** When true uses higher-quality (slower) blur — set for static/motion exports. */
    forExport?: boolean;
    /** Export-scoped preparation of immutable STATIC layers and mask regions. */
    staticCache?: StaticRenderCache;
  } = {}
): HTMLCanvasElement {
  if(isBaseCustomFrameTemplate(mockup.template))return renderBaseCustomFrame(userImage,options);
  const outputSize = mockup.template.outputSize || 1920;
  const size = Math.max(1, Math.round(options.renderSize || outputSize));
  const screenRenderScale = Math.max(1, options.screenRenderScale ?? 1);
  const screenLayerSize = Math.max(size, Math.round(size * screenRenderScale));
  const screenCount = getTemplateInputCount(mockup.template);
  const duo = normalizeFlexibleDisplay(mockup.template.flexibleDisplay);
  const isMultiScreen = screenCount > 1;
  const cache = !mockup.template.isVideo ? options.staticCache : undefined;
  const cachedCanvas = (key: string, dependencies: readonly unknown[], create: () => HTMLCanvasElement) =>
    cache ? cache.canvas(key, dependencies, create) : create();
  const activeMask = !duo && !isMultiScreen && options.useAlternateMask && mockup.mask2 ? mockup.mask2 : mockup.mask;
  const activeMaskChannel = !duo && !isMultiScreen && options.useAlternateMask && mockup.mask2
    ? mockup.mask2Channel || 'luminance'
    : mockup.maskChannel || 'luminance';
  const sharpenPackedMotionMasks = Boolean(mockup.template.isVideo);
  const combinedMaskNeeded =
    Boolean(options.diffuseOverlay) ||
    Boolean(mockup.template.transparency) ||
    Boolean(options.diffuse && !isNeutralDiffuseAdjustments(options.diffuse));
  const diffuseMask = combinedMaskNeeded && ((isMultiScreen && mockup.mask2) || activeMaskChannel !== 'luminance')
    ? cachedCanvas('combined-screen-mask', [
        mockup.mask, mockup.mask2, mockup.mask3, size,
        mockup.maskChannel, mockup.mask2Channel, mockup.mask3Channel
      ], () => createCombinedScreenMask([
        { source: mockup.mask, channel: mockup.maskChannel },
        ...(isMultiScreen && mockup.mask2 ? [{ source: mockup.mask2, channel: mockup.mask2Channel }] : []),
        ...(screenCount >= 3 && mockup.mask3
          ? [{ source: mockup.mask3, channel: mockup.mask3Channel }]
          : [])
      ], size))
    : activeMask;

  const master = createCanvas(size, size);
  const masterCtx = master.getContext('2d');
  if (!masterCtx) {
    throw new Error('2D context is not available for master canvas');
  }

  masterCtx.clearRect(0, 0, size, size);
  const drawBase = (ctx: CanvasRenderingContext2D) => {
    drawDiffuseLayer(ctx, mockup.base, diffuseMask, size, options.diffuse);
    if (options.diffuseOverlay) drawDiffuseOverlayLayer(ctx, options.diffuseOverlay, diffuseMask, size);
  };
  if (cache && (options.diffuseOverlay || (options.diffuse && !isNeutralDiffuseAdjustments(options.diffuse)))) {
    const preparedBase = cache.preparedCanvas('diffuse-base', [
      mockup.base, mockup.mask, mockup.mask2, mockup.mask3, size, activeMask, activeMaskChannel,
      mockup.maskChannel, mockup.mask2Channel, mockup.mask3Channel, screenCount,
      options.diffuseOverlay, JSON.stringify(options.diffuse)
    ], size, size, () => {
      const base = createCanvas(size, size);
      const ctx = base.getContext('2d');
      if (!ctx) throw new Error('2D context is not available for static export base');
      drawBase(ctx);
      return base;
    });
    if (preparedBase) masterCtx.drawImage(preparedBase, 0, 0);
    else drawBase(masterCtx);
  } else {
    drawBase(masterCtx);
  }

  if (userImage) {
    const quadSource =
      options.screenQuadOverride && options.screenQuadOverride.length === 4
        ? options.screenQuadOverride
        : mockup.template.screenQuad;
    const quad = scaleQuad(quadSource, screenLayerSize, outputSize);
    const warpMode = options.warpMode || (mockup.template.screenFit === 'contain' ? 'fit' : 'crop');
    const customTargetAspect = getDuoInputAspect(mockup.template) ?? getCategoryAspect(mockup.template.category) ?? getQuadAspect(quad);
    const screenSource =
      warpMode === 'custom'
        ? createCustomPlacementSource(userImage, customTargetAspect, options.customPlacement)
        : userImage;
    const reflection = options.reflection;

    if (shouldRenderReflection(mockup, reflection)) {
      const reflectionQuadSource =
        options.reflectionQuadOverride && options.reflectionQuadOverride.length === 4
          ? options.reflectionQuadOverride
          : mockup.template.reflectionQuad || [];
      const reflectionQuad = scaleQuad(reflectionQuadSource, screenLayerSize, outputSize);
      const reflectionSource = createReflectedSource(screenSource);
      const reflectionLayer = createWarpedScreenLayer(reflectionSource, reflectionQuad, {
        size: screenLayerSize,
        gridX: options.gridX ?? 30,
        gridY: options.gridY ?? 30,
        fitMode: warpMode === 'custom' ? 'fit' : warpMode,
        maskImage: null
      });
      const reflectionBlur = Math.max(0, reflection?.blur ?? 16);
      const processedReflectionLayer = processReflectionLayer(
        reflectionLayer,
        mockup.reflectionMask,
        mockup.reflectionBlurMask,
        reflectionBlur,
        Boolean(options.forExport),
        cache
      );

      const reflectionOpacity = clamp((reflection?.opacity ?? 50) / 100, 0, 1);
      masterCtx.save();
      masterCtx.globalAlpha = reflectionOpacity;
      masterCtx.drawImage(processedReflectionLayer, 0, 0, screenLayerSize, screenLayerSize, 0, 0, size, size);
      masterCtx.restore();
      releaseScopedCanvas(reflectionSource);
      releaseScopedCanvas(reflectionLayer);
      releaseScopedCanvas(processedReflectionLayer);
    }

    // Warp without mask so applyScreenBlurMask can blur real content pixels at screen edges.
    // For the non-blur path the mask is applied in-place below.
    const warpedScreen = createWarpedScreenLayer(screenSource, quad, {
      size: screenLayerSize,
      gridX: options.gridX ?? 30,
      gridY: options.gridY ?? 30,
      fitMode: warpMode === 'custom' ? 'fit' : warpMode,
      maskImage: null,
      duoRightQuad: duo?.mode === 'inner'
        ? scaleQuad(options.screenQuadOverride2 || mockup.template.screenQuad2 || [], screenLayerSize, outputSize)
        : undefined
    });

    let screenCanvas: HTMLCanvasElement;
    // Static templates run this in preview too, otherwise the Screen blur slider
    // gives no feedback until export. Motion keeps the export-only guard: its
    // preview is a scrubbed video and the pyramid would cost too much per frame.
    const screenBlurInPreview = !mockup.template.isVideo;
    if (mockup.screenBlurMask && (!options.realtime || screenBlurInPreview)) {
      screenCanvas = applyScreenBlurMask(
        warpedScreen,
        activeMask,
        mockup.screenBlurMask,
        options.screenOverlay?.blur ?? 50,
        activeMaskChannel,
        cache,
        sharpenPackedMotionMasks && activeMaskChannel !== 'luminance'
      );
    } else {
      applyMask(
        warpedScreen,
        activeMask,
        activeMaskChannel,
        cache,
        sharpenPackedMotionMasks && activeMaskChannel !== 'luminance'
      );
      screenCanvas = warpedScreen;
    }
    masterCtx.drawImage(screenCanvas, 0, 0, screenLayerSize, screenLayerSize, 0, 0, size, size);
    releaseScopedCanvas(warpedScreen);
    releaseScopedCanvas(screenCanvas);
    if (screenSource !== userImage) releaseScopedCanvas(screenSource);
  }

  const drawAdditionalScreen = (
    source: CanvasImageSource | null | undefined,
    mask: CanvasImageSource | null,
    maskChannel: MaskChannel,
    templateQuad: Point[] | undefined,
    quadOverride: Point[] | undefined,
    warpModeOption: ScreenWarpMode | undefined,
    customPlacement: CustomPlacement | null | undefined
  ): void => {
    if (!source || !mask || templateQuad?.length !== 4) return;
    const quadSource =
      quadOverride && quadOverride.length === 4
        ? quadOverride
        : templateQuad;
    const quad = scaleQuad(quadSource, screenLayerSize, outputSize);
    const warpMode = warpModeOption || (mockup.template.screenFit === 'contain' ? 'fit' : 'crop');
    const customTargetAspect = getDuoInputAspect(mockup.template, 2) ?? getCategoryAspect(mockup.template.category) ?? getQuadAspect(quad);
    const screenSource =
      warpMode === 'custom'
        ? createCustomPlacementSource(source, customTargetAspect, customPlacement)
        : source;
    const warpedScreen = createWarpedScreenLayer(screenSource, quad, {
      size: screenLayerSize,
      gridX: options.gridX ?? 30,
      gridY: options.gridY ?? 30,
      fitMode: warpMode === 'custom' ? 'fit' : warpMode,
      maskImage: null,
      duoRightQuad: duo?.mode === 'both'
        ? scaleQuad(options.screenQuadOverride3 || mockup.template.screenQuad3 || [], screenLayerSize, outputSize)
        : undefined
    });

    let screenCanvas: HTMLCanvasElement;
    const screenBlurInPreview = !mockup.template.isVideo;
    if (mockup.screenBlurMask && (!options.realtime || screenBlurInPreview)) {
      screenCanvas = applyScreenBlurMask(
        warpedScreen,
        mask,
        mockup.screenBlurMask,
        options.screenOverlay?.blur ?? 50,
        maskChannel,
        cache,
        sharpenPackedMotionMasks && maskChannel !== 'luminance'
      );
    } else {
      applyMask(
        warpedScreen,
        mask,
        maskChannel,
        cache,
        sharpenPackedMotionMasks && maskChannel !== 'luminance'
      );
      screenCanvas = warpedScreen;
    }
    masterCtx.drawImage(screenCanvas, 0, 0, screenLayerSize, screenLayerSize, 0, 0, size, size);
    releaseScopedCanvas(warpedScreen);
    releaseScopedCanvas(screenCanvas);
    if (screenSource !== source) releaseScopedCanvas(screenSource);
  };

  if (screenCount >= 2) {
    drawAdditionalScreen(
      options.userImage2,
      mockup.mask2,
      mockup.mask2Channel || 'luminance',
      mockup.template.screenQuad2,
      options.screenQuadOverride2,
      options.warpMode2,
      options.customPlacement2
    );
  }
  if (screenCount >= 3) {
    drawAdditionalScreen(
      options.userImage3,
      mockup.mask3,
      mockup.mask3Channel || 'luminance',
      mockup.template.screenQuad3,
      options.screenQuadOverride3,
      options.warpMode3,
      options.customPlacement3
    );
  }

  const screenOverlayOpacity = clamp((options.screenOverlay?.opacity ?? 50) / 100, 0, 1);
  if (screenOverlayOpacity > 0) {
    masterCtx.save();
    masterCtx.globalAlpha = screenOverlayOpacity;
    masterCtx.globalCompositeOperation = 'screen';
    masterCtx.drawImage(mockup.overlay, 0, 0, size, size);
    masterCtx.restore();
  }

  let transparencyMask: HTMLCanvasElement | null = null;
  if (mockup.template.transparency) {
    transparencyMask = cachedCanvas('transparency-mask', [
      mockup.base, mockup.mask, mockup.mask2, mockup.mask3, size, activeMask,
      mockup.maskChannel, mockup.mask2Channel, mockup.mask3Channel, screenCount
    ], () => createTransparencyMask(mockup.base, diffuseMask, size));
    masterCtx.save();
    masterCtx.globalCompositeOperation = 'destination-in';
    masterCtx.drawImage(transparencyMask, 0, 0, size, size);
    masterCtx.restore();
  }

  applyBackgroundTreatment(master, masterCtx, mockup.backgroundMask, size, options.background, cache);
  const separateBackground = options.separateCameraBackground && options.staticBackground &&
    options.staticBackground.mode !== 'none' && (mockup.backgroundMask || mockup.template.transparency);
  if (separateBackground) {
    if (mockup.backgroundMask && !options.background?.transparent) {
      const coverage = cachedCanvas('background-coverage', [mockup.backgroundMask, size],
        () => createMaskCoverageCanvas(mockup.backgroundMask!, size));
      masterCtx.save();
      masterCtx.globalCompositeOperation = 'destination-out';
      masterCtx.drawImage(coverage, 0, 0, size, size);
      masterCtx.restore();
    }
    // The shadow belongs to the device, not to the slower background layer.
    applyDeviceShadow(masterCtx, mockup, transparencyMask, size, options.deviceShadow, Boolean(options.useAlternateMask));
    // Master itself is the foreground: no duplicate 4K bitmap is retained.
    setDynamicZoomLayers(master, { background: options.staticBackground!.source });
    return master;
  }
  applyStaticBackground(
    masterCtx,
    mockup.backgroundMask,
    size,
    options.staticBackground,
    cache
  );
  applyDeviceShadow(
    masterCtx,
    mockup,
    transparencyMask,
    size,
    options.deviceShadow,
    Boolean(options.useAlternateMask)
  );

  return master;
}

interface DeviceShadowPresetDefinition {
  opacity: number;
  offset: number;
  blur: number;
  passes: number;
}

const DEVICE_SHADOW_PRESETS: Record<DeviceShadowPreset, DeviceShadowPresetDefinition> = {
  soft: { opacity: 0.16, offset: 0.022, blur: 0.022, passes: 5 },
  close: { opacity: 0.22, offset: 0.012, blur: 0.011, passes: 3 },
  floating: { opacity: 0.14, offset: 0.038, blur: 0.034, passes: 6 },
  long: { opacity: 0.17, offset: 0.058, blur: 0.024, passes: 5 }
};

const DEVICE_SHADOW_WORK_SIZE = 1024;
const DEVICE_SHADOW_CACHE_BYTE_LIMIT = 96 * 1024 * 1024;
const deviceShadowCache = new Map<string, HTMLCanvasElement>();
let deviceShadowCacheBytes = 0;

/**
 * Frees every cached device shadow. Called when the viewer moves to another
 * template: the cache is keyed by template, so its surfaces (up to 96 MB) only
 * ever serve the template being left. Safe between renders — renderMaster is
 * synchronous and draws a shadow the moment it takes it from here.
 */
export function releaseDeviceShadowCache(): void {
  deviceShadowCache.forEach(canvas => {
    canvas.width = 1;
    canvas.height = 1;
  });
  deviceShadowCache.clear();
  deviceShadowCacheBytes = 0;
}

function applyDeviceShadow(
  ctx: CanvasRenderingContext2D,
  mockup: LoadedMockupAssets,
  transparencyMask: HTMLCanvasElement | null,
  size: number,
  adjustments?: DeviceShadowAdjustments,
  useAlternateMask: boolean = false
): void {
  if (!adjustments?.enabled || (!mockup.backgroundMask && !mockup.template.transparency)) {
    return;
  }

  const preset = DEVICE_SHADOW_PRESETS[adjustments.preset] || DEVICE_SHADOW_PRESETS.soft;
  const angle = normalizeAngle(adjustments.angle);
  const opacity = Math.max(0, Math.min(100, adjustments.opacity)) / 100;
  const color = normalizeShadowColor(adjustments.color);
  const cacheKey = [
    mockup.template.id,
    mockup.template.updatedAt || '',
    size,
    adjustments.preset,
    angle,
    color,
    mockup.template.transparency ? 'alpha' : 'background-mask',
    useAlternateMask ? 'alternate' : 'primary'
  ].join('|');
  const pngOperation = isCanvasMemoryScopeActive();
  let shadow = pngOperation ? undefined : deviceShadowCache.get(cacheKey);

  if (!shadow) {
    const foregroundMask = transparencyMask || createForegroundCoverageCanvas(
      mockup.backgroundMask || mockup.base,
      size
    );
    shadow = createDeviceShadowLayer(foregroundMask, size, preset, angle, color);
    if (foregroundMask !== transparencyMask) releaseScopedCanvas(foregroundMask);
    if (!pngOperation) {
      const shadowBytes = shadow.width * shadow.height * 4;
      while (deviceShadowCache.size > 0 && deviceShadowCacheBytes + shadowBytes > DEVICE_SHADOW_CACHE_BYTE_LIMIT) {
        const oldestKey = deviceShadowCache.keys().next().value as string | undefined;
        if (!oldestKey) break;
        const oldest = deviceShadowCache.get(oldestKey);
        if (oldest) {
          deviceShadowCacheBytes -= oldest.width * oldest.height * 4;
          oldest.width = 1;
          oldest.height = 1;
        }
        deviceShadowCache.delete(oldestKey);
      }
      deviceShadowCache.set(cacheKey, shadow);
      deviceShadowCacheBytes += shadowBytes;
    }
  }

  ctx.save();
  ctx.globalAlpha *= opacity;
  ctx.drawImage(shadow, 0, 0, size, size);
  ctx.restore();
  releaseScopedCanvas(shadow);
}

function createDeviceShadowLayer(
  foregroundMask: HTMLCanvasElement,
  size: number,
  preset: DeviceShadowPresetDefinition,
  angle: number,
  color: string
): HTMLCanvasElement {
  const workSize = Math.min(size, DEVICE_SHADOW_WORK_SIZE);
  const workScale = workSize / size;
  const source = createCanvas(workSize, workSize);
  const sourceCtx = source.getContext('2d');
  if (!sourceCtx) {
    return createCanvas(size, size);
  }

  sourceCtx.fillStyle = color;
  sourceCtx.fillRect(0, 0, workSize, workSize);
  sourceCtx.globalCompositeOperation = 'destination-in';
  sourceCtx.drawImage(foregroundMask, 0, 0, workSize, workSize);

  const blurPx = Math.max(1, preset.blur * size * workScale);
  const softened = createUnclippedDeviceShadowBlur(source, blurPx, color);

  const radians = (angle * Math.PI) / 180;
  const offset = preset.offset * size;
  const offsetX = Math.cos(radians) * offset;
  const offsetY = Math.sin(radians) * offset;
  const shadow = createCanvas(size, size);
  const shadowCtx = shadow.getContext('2d');
  if (!shadowCtx) {
    return shadow;
  }

  shadowCtx.globalAlpha = preset.opacity;
  shadowCtx.drawImage(
    softened,
    0,
    0,
    workSize,
    workSize,
    offsetX,
    offsetY,
    size,
    size
  );
  shadowCtx.globalAlpha = 1;
  shadowCtx.globalCompositeOperation = 'destination-out';
  shadowCtx.drawImage(foregroundMask, 0, 0, size, size);
  releaseScopedCanvas(source);
  if (softened !== source) releaseScopedCanvas(softened);
  return shadow;
}

function normalizeShadowColor(value: string): string {
  return /^#[0-9a-f]{6}$/i.test(value) ? value : '#000000';
}

function createUnclippedDeviceShadowBlur(
  source: HTMLCanvasElement,
  blurPx: number,
  color: string
): HTMLCanvasElement {
  if (!shouldUseApproximateCanvasBlur()) {
    const blurred = createCanvas(source.width, source.height);
    const ctx = blurred.getContext('2d');
    if (!ctx) {
      return source;
    }
    ctx.filter = `blur(${Math.max(1, blurPx).toFixed(2)}px)`;
    ctx.drawImage(source, 0, 0);
    return blurred;
  }

  // Where ctx.filter cannot be trusted (iOS Safari, Android, Windows Chromium)
  // the shadow used to be softened by a few downscale/upscale passes. Those
  // passes spread it by roughly the downscale factor — about 2px — while the
  // desktop filter spreads it by blurPx, around 22px for Soft, so phones got a
  // hard shadow. The blur is computed here instead: the layer is one colour
  // with a varying alpha, so blurring alpha alone reproduces the filter.
  return createGaussianShadowLayer(source, blurPx, color);
}

function createGaussianShadowLayer(
  source: HTMLCanvasElement,
  sigma: number,
  color: string
): HTMLCanvasElement {
  const width = source.width;
  const height = source.height;
  const sourceCtx = source.getContext('2d');
  const output = createCanvas(width, height);
  const outputCtx = output.getContext('2d');
  if (!sourceCtx || !outputCtx) {
    return source;
  }

  const pixels = sourceCtx.getImageData(0, 0, width, height).data;
  const alpha = new Float32Array(width * height);
  for (let index = 0; index < alpha.length; index += 1) {
    alpha[index] = pixels[index * 4 + 3];
  }
  const scratch = new Float32Array(alpha.length);
  // Three box passes per axis approximate a Gaussian to within a few percent;
  // everything past the edge counts as transparent, like the canvas filter.
  const lineLength = Math.max(width, height);
  const prefix = new Float32Array(lineLength + 1);
  for (const boxWidth of getGaussianBoxWidths(Math.max(1, sigma), 3)) {
    boxBlurAlpha(alpha, scratch, width, height, boxWidth, true, prefix);
    boxBlurAlpha(scratch, alpha, width, height, boxWidth, false, prefix);
  }

  const shadow = parseShadowRgb(color);
  const image = outputCtx.createImageData(width, height);
  const out = image.data;
  for (let index = 0; index < alpha.length; index += 1) {
    const offset = index * 4;
    out[offset] = shadow[0];
    out[offset + 1] = shadow[1];
    out[offset + 2] = shadow[2];
    out[offset + 3] = Math.round(alpha[index]);
  }
  outputCtx.putImageData(image, 0, 0);
  return output;
}

/** Box widths whose repeated application matches a Gaussian of `sigma`. */
function getGaussianBoxWidths(sigma: number, count: number): number[] {
  const ideal = Math.sqrt((12 * sigma * sigma) / count + 1);
  let lower = Math.floor(ideal);
  if (lower % 2 === 0) lower -= 1;
  const upper = lower + 2;
  const lowerCount = Math.round(
    (12 * sigma * sigma - count * lower * lower - 4 * count * lower - 3 * count) / (-4 * lower - 4)
  );
  return Array.from({ length: count }, (_, pass) => (pass < lowerCount ? lower : upper));
}

function boxBlurAlpha(
  input: Float32Array,
  output: Float32Array,
  width: number,
  height: number,
  boxWidth: number,
  horizontal: boolean,
  prefix: Float32Array
): void {
  const radius = (boxWidth - 1) / 2;
  const lines = horizontal ? height : width;
  const length = horizontal ? width : height;
  const stepAlong = horizontal ? 1 : width;
  const stepAcross = horizontal ? width : 1;
  for (let line = 0; line < lines; line += 1) {
    const start = line * stepAcross;
    prefix[0] = 0;
    for (let position = 0; position < length; position += 1) {
      prefix[position + 1] = prefix[position] + input[start + position * stepAlong];
    }
    for (let position = 0; position < length; position += 1) {
      const from = Math.max(0, position - radius);
      const to = Math.min(length, position + radius + 1);
      output[start + position * stepAlong] = (prefix[to] - prefix[from]) / boxWidth;
    }
  }
}

function parseShadowRgb(color: string): [number, number, number] {
  const value = parseInt(normalizeShadowColor(color).slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function createForegroundCoverageCanvas(mask: CanvasImageSource, size: number): HTMLCanvasElement {
  const canvas = createCanvas(size, size);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return canvas;
  }

  ctx.drawImage(mask, 0, 0, size, size);
  const imageData = ctx.getImageData(0, 0, size, size);
  const pixels = imageData.data;
  for (let index = 0; index < pixels.length; index += 4) {
    const luminance = getPerceivedLuminance(pixels[index], pixels[index + 1], pixels[index + 2]);
    const alpha = Math.round(luminance * (pixels[index + 3] / 255) * 255);
    pixels[index] = 0;
    pixels[index + 1] = 0;
    pixels[index + 2] = 0;
    pixels[index + 3] = alpha;
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

function normalizeAngle(value: number): number {
  if (!Number.isFinite(value)) {
    return 45;
  }
  return Math.round(((value % 360) + 360) % 360);
}

function applyStaticBackground(
  ctx: CanvasRenderingContext2D,
  backgroundMask: CanvasImageSource | null,
  size: number,
  composition?: StaticBackgroundComposition & { source: CanvasImageSource },
  cache?: StaticRenderCache
): void {
  if (!composition || composition.mode === 'none') {
    return;
  }

  if (backgroundMask) {
    const maskCanvas = cache
      ? cache.canvas('background-coverage', [backgroundMask, size], () => createMaskCoverageCanvas(backgroundMask, size))
      : createMaskCoverageCanvas(backgroundMask, size);
    // Image alpha and uncovered custom placement areas reveal the processed template background.
    if (composition.mode !== 'image') {
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.drawImage(maskCanvas, 0, 0, size, size);
      ctx.restore();
    }

    const replacement = createCanvas(size, size);
    const replacementCtx = replacement.getContext('2d');
    if (!replacementCtx) {
      return;
    }
    replacementCtx.drawImage(composition.source, 0, 0, size, size);
    replacementCtx.globalCompositeOperation = 'destination-in';
    replacementCtx.drawImage(maskCanvas, 0, 0, size, size);
    ctx.drawImage(replacement, 0, 0);
    releaseScopedCanvas(maskCanvas);
    releaseScopedCanvas(replacement);
    return;
  }

  ctx.save();
  ctx.globalCompositeOperation = 'destination-over';
  ctx.drawImage(composition.source, 0, 0, size, size);
  ctx.restore();
}

function createCombinedScreenMask(
  masks: Array<{ source: CanvasImageSource; channel?: MaskChannel }>,
  size: number
): HTMLCanvasElement {
  const combined = createCanvas(size, size);
  const ctx = combined.getContext('2d');
  if (!ctx || masks.length === 0) {
    return combined;
  }

  if (masks.every((mask) => (mask.channel || 'luminance') === 'luminance')) {
    ctx.drawImage(masks[0].source, 0, 0, size, size);
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (const mask of masks.slice(1)) {
      ctx.drawImage(mask.source, 0, 0, size, size);
    }
    ctx.restore();
    return combined;
  }

  const sourceData = new Map<CanvasImageSource, ImageData>();
  for (const mask of masks) {
    if (sourceData.has(mask.source)) continue;
    const sourceCanvas = createCanvas(size, size);
    const sourceCtx = sourceCanvas.getContext('2d');
    if (!sourceCtx) return combined;
    sourceCtx.drawImage(mask.source, 0, 0, size, size);
    sourceData.set(mask.source, sourceCtx.getImageData(0, 0, size, size));
    releaseScopedCanvas(sourceCanvas);
  }
  const output = ctx.createImageData(size, size);
  for (let index = 0; index < output.data.length; index += 4) {
    let factor = 0;
    for (const mask of masks) {
      factor = Math.max(
        factor,
        getMaskFactor(sourceData.get(mask.source)!.data, index, mask.channel || 'luminance')
      );
    }
    const value = Math.round(factor * 255);
    output.data[index] = value;
    output.data[index + 1] = value;
    output.data[index + 2] = value;
    output.data[index + 3] = 255;
  }
  ctx.putImageData(output, 0, 0);
  return combined;
}

function shouldRenderReflection(
  mockup: LoadedMockupAssets,
  reflection?: ReflectionAdjustments
): boolean {
  return Boolean(
    mockup.template.reflectionEnabled &&
      reflection?.enabled !== false &&
      mockup.template.reflectionQuad &&
      mockup.template.reflectionQuad.length === 4
  );
}

function createWarpedScreenLayer(
  source: CanvasImageSource,
  quad: Point[],
  options: {
    size: number;
    gridX: number;
    gridY: number;
    fitMode: ScreenWarpMode;
    maskImage: CanvasImageSource | null;
    duoRightQuad?: Point[];
  }
): HTMLCanvasElement {
  const screenCanvas = createCanvas(options.size, options.size);
  const screenCtx = screenCanvas.getContext('2d');
  if (!screenCtx) {
    throw new Error('2D context is not available for screen canvas');
  }

  if (options.duoRightQuad) {
    warpImageToDuo(screenCtx, source, quad, options.duoRightQuad, options);
  } else {
    warpImageToQuad(screenCtx, source, quad, {
    gridX: options.gridX,
    gridY: options.gridY,
    fitMode: options.fitMode,
    sourceMaxSize: options.size
    });
    healTransparentSeams(screenCanvas, 2);
  }
  if (options.maskImage) {
    applyMask(screenCanvas, options.maskImage);
  }

  return screenCanvas;
}

/**
 * Applies a B&W blur mask to the user's screen canvas in-place.
 * White pixels in the mask = maximum blur on the screen content, blur bleeds
 * outside the cutout mask edges. Black = no blur. Skipped in realtime playback.
 */
/**
 * Blends between the sharp original and a blurred version using a single B&W mask.
 * canvas   – already-masked warped screen canvas (activeMask applied).
 * blurMask – atlas tile (0,1): black = sharp (no blur), white = maximum blur.
 * Returns a new canvas (does not modify the input).
 */
function applyScreenBlurMask(
  unmaskedCanvas: HTMLCanvasElement,
  screenMask: CanvasImageSource,
  blurMask: CanvasImageSource,
  blurAmount: number = 50,
  screenMaskChannel: MaskChannel = 'luminance',
  cache?: StaticRenderCache,
  sharpenPackedMotionEdge = false
): HTMLCanvasElement {
  const w = unmaskedCanvas.width;
  const h = unmaskedCanvas.height;
  // The slider is centred: 50 reproduces the radius this effect shipped with,
  // 100 doubles it. Same 0.1-100 range as the reflection blur slider.
  const strength = clamp(blurAmount, 0.1, 100) / 50;
  const baseBlurPx = Math.max(4, Math.round(Math.min(w, h) * 0.007));
  const maxBlurPx = Math.max(0.5, baseBlurPx * strength);
  const preparedMask = cache?.mask(screenMask, w, h, screenMaskChannel);
  const region = preparedMask ?? { x: 0, y: 0, width: w, height: h };
  if (!region.width || !region.height) return createCanvas(w, h);

  // Geometric blur pyramid, same principle as processReflectionLayer.
  //
  // The old code cross-faded the SHARP source against a single fully blurred
  // copy. That is a dissolve, not a variable-radius blur: at a mid-grey mask
  // value half of the sharp image survives at half opacity, so edges stay
  // crisp and the result reads as haze. Only pure white looked right, because
  // only there did the sharp layer drop out completely.
  //
  // Mixing two ADJACENT blur levels instead means no sharp pixels are left
  // anywhere the mask asks for blur. Level 0 is the sharp source because black
  // on the mask must leave the content untouched — unlike reflections, whose
  // floor is 0.45R. The levels grow geometrically so neighbouring ones stay
  // close and each pairwise mix reads as a genuine intermediate blur.
  const { levels: pyramid, topPosition } = createBlurPyramid(unmaskedCanvas, maxBlurPx);
  if (pyramid.length === 0) {
    const fallback = cloneCanvas(unmaskedCanvas);
    applyMask(
      fallback,
      screenMask,
      cache ? screenMaskChannel : 'luminance',
      cache,
      sharpenPackedMotionEdge
    );
    return fallback;
  }

  const output = createCanvas(w, h);
  const outputCtx = output.getContext('2d');
  const sharpCtx = unmaskedCanvas.getContext('2d');
  if (!outputCtx || !sharpCtx) return unmaskedCanvas;

  let smPixels: Uint8ClampedArray;
  let bmPixels: Uint8ClampedArray;
  if (cache && preparedMask) {
    smPixels = preparedMask.data;
    bmPixels = cache.pixels(blurMask, w, h, region);
  } else {
    const smCanvas = createCanvas(w, h);
    const bmCanvas = createCanvas(w, h);
    const smCtx = smCanvas.getContext('2d');
    const bmCtx = bmCanvas.getContext('2d');
    if (!smCtx || !bmCtx) return unmaskedCanvas;
    smCtx.drawImage(screenMask, 0, 0, w, h);
    bmCtx.drawImage(blurMask, 0, 0, w, h);
    smPixels = smCtx.getImageData(0, 0, w, h).data;
    bmPixels = bmCtx.getImageData(0, 0, w, h).data;
    releaseScopedCanvas(smCanvas);
    releaseScopedCanvas(bmCanvas);
  }

  // The accumulator starts at pyramid level 0 and walks up one level per pass.
  // Only two full-size pixel buffers are alive at a time — holding all five at
  // once costs ~160 MB at 2880px and is what used to crash iOS Safari tabs.
  const acc = sharpCtx.getImageData(region.x, region.y, region.width, region.height);
  const accPixels = acc.data;

  for (let step = 0; step < pyramid.length; step++) {
    // The pyramid is built from the UNMASKED canvas so the blur averages real
    // content at the screen boundary instead of transparent zeros (which would
    // feather, not blur). Mobile levels arrive at work resolution and are only
    // upscaled here, one at a time, to keep peak memory at two full-size buffers.
    const layer = materializeBlurLevel(pyramid[step], unmaskedCanvas);
    // Only CPU readback/mixing is cropped. The blur pyramid above still uses
    // the complete unmasked image, preserving blur around the screen edges.
    const layerPixels = layer.getContext('2d')?.getImageData(region.x, region.y, region.width, region.height).data;
    if (!layerPixels) continue;

    for (let i = 0; i < accPixels.length; i += 4) {
      const bmT = (bmPixels[i] + bmPixels[i + 1] + bmPixels[i + 2]) / (255 * 3) * (bmPixels[i + 3] / 255);
      const smoothT = bmT * bmT * (3 - 2 * bmT); // smoothstep
      const ramp = smoothT * topPosition - step;

      if (ramp <= 0) continue; // this pixel never reaches the current level

      if (ramp >= 1) {
        // Fully at or above this level: take it verbatim, no rounding error.
        accPixels[i] = layerPixels[i];
        accPixels[i + 1] = layerPixels[i + 1];
        accPixels[i + 2] = layerPixels[i + 2];
        continue;
      }

      const keep = 1 - ramp;
      accPixels[i] = accPixels[i] * keep + layerPixels[i] * ramp;
      accPixels[i + 1] = accPixels[i + 1] * keep + layerPixels[i + 1] * ramp;
      accPixels[i + 2] = accPixels[i + 2] * keep + layerPixels[i + 2] * ramp;
    }
    if (layer !== unmaskedCanvas) releaseScopedCanvas(layer);
  }

  // Alpha is never touched above, so it still holds the sharp silhouette here.
  // screenMask luminance cuts the screen shape out of it.
  for (let i = 3; i < accPixels.length; i += 4) {
    const rawFactor = getMaskFactor(smPixels, i - 3, screenMaskChannel);
    const smFactor = sharpenPackedMotionEdge ? sharpenPackedMotionMaskFactor(rawFactor) : rawFactor;
    accPixels[i] = accPixels[i] * smFactor;
  }

  outputCtx.putImageData(acc, region.x, region.y);
  pyramid.forEach(layer => { if (layer !== unmaskedCanvas) releaseScopedCanvas(layer); });
  return output;
}

function processReflectionLayer(
  reflectionCanvas: HTMLCanvasElement,
  visibilityMask: CanvasImageSource | null,
  blurMask: CanvasImageSource | null,
  blurAmount: number,
  forExport: boolean = false,
  cache?: StaticRenderCache
): HTMLCanvasElement {
  // Fast path: skip all blur passes for negligible blur values.
  // resolveReflectionBlurPx clamps blurAmount to 0.1 minimum, so values < 5
  // produce < 3.75 px of real blur — effectively invisible at normal viewing sizes.
  if (blurAmount < 5) {
    const maskedSource = cloneCanvas(reflectionCanvas);
    releaseScopedCanvas(reflectionCanvas);
    if (visibilityMask) {
      applyMask(maskedSource, visibilityMask, 'luminance', cache);
    }
    return maskedSource;
  }

  const normalizedMaxBlur = resolveReflectionBlurPx(reflectionCanvas, blurAmount);
  const maskedSource = cloneCanvas(reflectionCanvas);
  releaseScopedCanvas(reflectionCanvas);
  if (visibilityMask) {
    applyMask(maskedSource, visibilityMask, 'luminance', cache);
  }

  const minimumBlur = normalizedMaxBlur > 0 ? Math.max(1.5, normalizedMaxBlur * 0.45) : 0;
  const baseLayer = minimumBlur > 0 ? createBlurredCanvas(maskedSource, minimumBlur, forExport) : maskedSource;
  const strongerBlurLayer =
    normalizedMaxBlur > minimumBlur ? createBlurredCanvas(maskedSource, normalizedMaxBlur, forExport) : baseLayer;
  if (maskedSource !== baseLayer && maskedSource !== strongerBlurLayer) releaseScopedCanvas(maskedSource);
  const mixedLayer =
    blurMask && normalizedMaxBlur > minimumBlur
      ? blendReflectionBlurByMask(baseLayer, strongerBlurLayer, blurMask, cache, visibilityMask)
      : baseLayer;
  if (baseLayer !== mixedLayer) releaseScopedCanvas(baseLayer);
  if (strongerBlurLayer !== mixedLayer) releaseScopedCanvas(strongerBlurLayer);

  const transitionBlur = blurMask && normalizedMaxBlur > 0 ? Math.min(2, normalizedMaxBlur * 0.12) : 0;
  const softenedLayer = transitionBlur > 0 ? createBlurredCanvas(mixedLayer, transitionBlur, forExport) : mixedLayer;
  if (mixedLayer !== softenedLayer) releaseScopedCanvas(mixedLayer);

  if (visibilityMask) {
    applyMask(softenedLayer, visibilityMask, 'luminance', cache);
  }

  return softenedLayer;
}

function createReflectedSource(source: CanvasImageSource): HTMLCanvasElement {
  const { width, height } = getSourceSize(source);
  const canvas = createCanvas(width, height);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('2D context is not available for reflection canvas');
  }

  ctx.save();
  ctx.translate(0, height);
  ctx.scale(1, -1);
  ctx.drawImage(source, 0, 0, width, height);
  ctx.restore();
  return canvas;
}

function createBlurredCanvas(source: HTMLCanvasElement, blurPx: number, forExport: boolean = false): HTMLCanvasElement {
  if (blurPx <= 0.01) {
    return cloneCanvas(source);
  }

  if (shouldUseApproximateCanvasBlur()) {
    return createApproximateBlurredCanvas(source, blurPx, forExport);
  }

  const canvas = createCanvas(source.width, source.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return cloneCanvas(source);
  }

  ctx.save();
  ctx.filter = `blur(${Math.max(0, blurPx).toFixed(2)}px)`;
  ctx.drawImage(source, 0, 0);
  ctx.restore();

  // Keep the original alpha silhouette so blur affects detail, not overall visibility.
  ctx.save();
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(source, 0, 0);
  ctx.restore();

  return canvas;
}

function shouldUseApproximateCanvasBlur(): boolean {
  // Windows Chromium: native ctx.filter blur is unreliable.
  // Mobile (iOS Safari / Android Chrome): native ctx.filter blur silently
  // fails on large canvases (>~2K) and in combination with destination-in,
  // so we route through a small-canvas resample path that uses native blur
  // on a 1024px working buffer (where it works reliably) then upsamples.
  return isWindowsChromiumCanvasRuntime() || isMobileCanvasRuntime();
}

function createApproximateBlurredCanvas(source: HTMLCanvasElement, blurPx: number, forExport: boolean = false): HTMLCanvasElement {
  if (isMobileCanvasRuntime()) {
    return createMobileApproximateBlurredCanvas(source, blurPx, forExport);
  }

  return createWindowsApproximateBlurredCanvas(source, blurPx);
}

function createWindowsApproximateBlurredCanvas(source: HTMLCanvasElement, blurPx: number): HTMLCanvasElement {
  const width = source.width;
  const height = source.height;
  const passes = blurPx >= 18 ? 3 : blurPx >= 8 ? 2 : 1;
  let current = source;

  for (let pass = 0; pass < passes; pass += 1) {
    const downscale = Math.max(1.25, Math.min(6, 1 + blurPx / 6 + pass * 0.35));
    current = createResampledBlurLayer(current, width, height, downscale, false);
  }

  return applySourceAlphaMask(current, source);
}

function createMobileApproximateBlurredCanvas(
  source: HTMLCanvasElement,
  blurPx: number,
  forExport: boolean = false
): HTMLCanvasElement {
  // iOS Safari / Android Chrome strategy — pure iterative downscale on a SMALL
  // working canvas. We deliberately avoid ctx.filter='blur()' because on iOS
  // Safari it silently no-ops in many contexts (especially after destination-in
  // or on canvases used in a multi-pass pipeline). Each downscale+upscale cycle
  // is a bilinear low-pass — chaining 3-7 of them composes into a strong,
  // smooth, Gaussian-like blur. Working at ≤1024px keeps memory bounded
  // (no 4K-canvas chain), so iOS doesn't OOM on export resolutions either.
  const fullW = source.width;
  const fullH = source.height;

  // Working canvas size — bigger when exporting (better fidelity, still safe).
  // For very large workBlur (extreme exportBoost), also shrink working canvas
  // — the initial downsample is itself a strong low-pass, multiplying the
  // effective blur on top of the iterative passes.
  const requestedBlurEstimate = blurPx * (forExport ? 50.0 : 1.0);
  const maxSide = forExport
    ? requestedBlurEstimate >= 1500 ? 480
      : requestedBlurEstimate >= 600 ? 640
      : requestedBlurEstimate >= 250 ? 800
      : 1024
    : 720;
  const longest = Math.max(fullW, fullH);
  const scale = longest > maxSide ? maxSide / longest : 1;
  const workW = Math.max(8, Math.round(fullW * scale));
  const workH = Math.max(8, Math.round(fullH * scale));

  // Step 1: downsample to working size (high-quality bilinear).
  let current = createCanvas(workW, workH);
  const initCtx = current.getContext('2d');
  if (!initCtx) return cloneCanvas(source);
  initCtx.imageSmoothingEnabled = true;
  initCtx.imageSmoothingQuality = 'high';
  initCtx.drawImage(source, 0, 0, workW, workH);

  // Step 2: iterative downscale-upscale passes on the small canvas.
  // Each pass = downscale by ~1.5× → upscale back → bilinear smooths.
  // Chaining N passes is mathematically close to a Gaussian.
  // Effective "blur intensity" in work space.
  const exportBoost = forExport ? 50.0 : 1.0;
  const workBlur = Math.max(1, blurPx * scale * exportBoost);
  const passes = workBlur >= 400 ? 18 : workBlur >= 250 ? 16 : workBlur >= 150 ? 14 : workBlur >= 80 ? 12 : workBlur >= 50 ? 10 : workBlur >= 30 ? 9 : workBlur >= 18 ? 8 : workBlur >= 12 ? 6 : workBlur >= 7 ? 5 : workBlur >= 4 ? 4 : 3;

  // Per-pass downscale base scales with workBlur intensity — bigger requested
  // blur ⇒ more aggressive resample per pass ⇒ visibly stronger total blur.
  // Caps at 2.6× to avoid bilinear blockiness even on iOS.
  const downscaleBase = Math.min(2.4, 1.5 + Math.max(0, workBlur - 8) / 22);
  for (let pass = 0; pass < passes; pass += 1) {
    const downscale = Math.min(2.6, downscaleBase + pass * 0.12);
    const previous = current;
    current = createResampledBlurLayer(current, workW, workH, downscale, true);
    if (previous !== current) releaseScopedCanvas(previous);
  }

  // Step 3: upsample back to full resolution (high-quality bilinear).
  const out = createCanvas(fullW, fullH);
  const octx = out.getContext('2d');
  if (!octx) return cloneCanvas(source);
  octx.imageSmoothingEnabled = true;
  octx.imageSmoothingQuality = 'high';
  octx.drawImage(current, 0, 0, workW, workH, 0, 0, fullW, fullH);

  // Step 4: trim to source alpha silhouette in-place (no extra full-size canvas).
  octx.save();
  octx.globalCompositeOperation = 'destination-in';
  octx.drawImage(source, 0, 0);
  octx.restore();
  releaseScopedCanvas(current);

  return out;
}



/**
 * Ascending-strength blur levels for applyScreenBlurMask, plus where the mask's
 * white point sits on that ladder.
 *
 * Desktop keeps independent Gaussians at radii that double each step.
 *
 * Mobile cannot use ctx.filter at all, so its only blur primitive is resolution
 * reduction. Measured on a step edge (sigma from the second moment of the edge
 * derivative), two things decide the design:
 *
 *  - Chaining resample passes at a fixed working size SATURATES. Six passes at
 *    downscale 1.5 only reach sigma 1.4px; the requested radius barely moves the
 *    result. That is why the old chain showed one flat blur with no gradient.
 *  - A single arbitrary downscale is quantised by the browser's internal mip
 *    chain: downscale 22.4x measured sigma 6.9 instead of 13, a 47% miss, while
 *    11.2x and 44.8x landed within 1%.
 *
 * Exact halving is the one step that behaves: sigma measured 0.70, 1.25, 2.50,
 * 4.98, 9.95, 19.91 for one through six halvings — a clean doubling per level,
 * which is exactly the geometric ramp this effect wants. So the mobile pyramid
 * IS the mip chain, and the mask's white point maps to a possibly fractional
 * position on it, letting the existing pairwise blend hit the requested radius.
 *
 * Levels stay at mip resolution — materialise them one at a time.
 * Reflections keep using createBlurredCanvas untouched; their look is signed off.
 */
interface BlurPyramid {
  /** Ascending blur strength. Index i is one step stronger than i-1. */
  levels: HTMLCanvasElement[];
  /** Level position the mask's white point maps to. May be fractional. */
  topPosition: number;
}

/**
 * Measured constant: mip level k lands on ~0.389 * 2^k px of blur at full
 * resolution, once materializeBlurLevel grows it back by doubling. (A direct
 * upscale gives 0.311 but shows facets — see materializeBlurLevel.)
 */
const MIP_SIGMA_BASE = 0.389;

function createBlurPyramid(source: HTMLCanvasElement, maxBlurPx: number): BlurPyramid {
  if (!shouldUseApproximateCanvasBlur()) {
    const levelCount = 4;
    const levels: HTMLCanvasElement[] = [];
    for (let level = 0; level < levelCount; level += 1) {
      levels.push(createBlurredCanvas(source, maxBlurPx / Math.pow(2, levelCount - 1 - level)));
    }
    return { levels, topPosition: levelCount };
  }

  // Halving six times already reaches ~20px of blur; a seventh step lands on a
  // canvas small enough (15px wide at 1920) that edge effects take over.
  const topPosition = clamp(Math.log2(Math.max(maxBlurPx, MIP_SIGMA_BASE * 2) / MIP_SIGMA_BASE), 1, 6);
  const levelCount = Math.ceil(topPosition);

  const levels: HTMLCanvasElement[] = [];
  let current = source;
  for (let level = 0; level < levelCount; level += 1) {
    const width = Math.max(2, Math.round(current.width / 2));
    const height = Math.max(2, Math.round(current.height / 2));
    const mip = createCanvas(width, height);
    const ctx = mip.getContext('2d');
    if (!ctx) {
      break;
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(current, 0, 0, current.width, current.height, 0, 0, width, height);
    levels.push(mip);
    current = mip;
  }

  return { levels, topPosition: Math.min(topPosition, levels.length) };
}

/** Upscales a work-resolution pyramid level back to the source size, once it is needed. */
function materializeBlurLevel(level: HTMLCanvasElement, source: HTMLCanvasElement): HTMLCanvasElement {
  if (level.width === source.width && level.height === source.height) {
    return level;
  }

  // Grow back by doubling rather than in one jump. Bilinear upscaling straight
  // from a deep mip (30px wide at 1920) only guarantees C0 continuity, so the
  // reconstruction shows the tent patches as blurry quadrilateral facets — the
  // "squares" visible at strong blur. Each 2x step keeps the interpolation local
  // and composes into a far smoother reconstruction.
  //
  // Measured side effect: doubling adds ~25% sigma versus a direct jump
  // (4.98 -> 6.20, 9.95 -> 12.44, 19.91 -> 24.95). The ratio between levels stays
  // 2.006, so the ladder is still geometric — only MIP_SIGMA_BASE moves.
  let current = level;
  while (current.width * 2 < source.width) {
    const width = Math.min(source.width, current.width * 2);
    const height = Math.min(source.height, Math.max(1, current.height * 2));
    const step = createCanvas(width, height);
    const stepCtx = step.getContext('2d');
    if (!stepCtx) {
      break;
    }
    stepCtx.imageSmoothingEnabled = true;
    stepCtx.imageSmoothingQuality = 'high';
    stepCtx.drawImage(current, 0, 0, current.width, current.height, 0, 0, width, height);
    current = step;
  }

  const out = createCanvas(source.width, source.height);
  const ctx = out.getContext('2d');
  if (!ctx) {
    return level;
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(current, 0, 0, current.width, current.height, 0, 0, source.width, source.height);
  ctx.save();
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(source, 0, 0);
  ctx.restore();
  return out;
}

function createResampledBlurLayer(
  source: HTMLCanvasElement,
  width: number,
  height: number,
  downscale: number,
  highQuality: boolean
): HTMLCanvasElement {
  const clampedDownscale = Math.max(1.01, downscale);
  const smallWidth = Math.max(1, Math.round(width / clampedDownscale));
  const smallHeight = Math.max(1, Math.round(height / clampedDownscale));
  const downsampled = createCanvas(smallWidth, smallHeight);
  const downsampledCtx = downsampled.getContext('2d');
  const upsampled = createCanvas(width, height);
  const upsampledCtx = upsampled.getContext('2d');

  if (!downsampledCtx || !upsampledCtx) {
    return cloneCanvas(source);
  }

  const smoothing: ImageSmoothingQuality = highQuality ? 'high' : 'medium';
  downsampledCtx.imageSmoothingEnabled = true;
  downsampledCtx.imageSmoothingQuality = smoothing;
  upsampledCtx.imageSmoothingEnabled = true;
  upsampledCtx.imageSmoothingQuality = smoothing;

  downsampledCtx.clearRect(0, 0, smallWidth, smallHeight);
  downsampledCtx.drawImage(source, 0, 0, width, height, 0, 0, smallWidth, smallHeight);
  upsampledCtx.clearRect(0, 0, width, height);
  upsampledCtx.drawImage(downsampled, 0, 0, smallWidth, smallHeight, 0, 0, width, height);
  releaseScopedCanvas(downsampled);
  return upsampled;
}

function applySourceAlphaMask(layer: HTMLCanvasElement, source: HTMLCanvasElement): HTMLCanvasElement {
  const output = createCanvas(layer.width, layer.height);
  const outputCtx = output.getContext('2d');
  if (!outputCtx) {
    return cloneCanvas(layer);
  }

  outputCtx.drawImage(layer, 0, 0);
  outputCtx.save();
  outputCtx.globalCompositeOperation = 'destination-in';
  outputCtx.drawImage(source, 0, 0);
  outputCtx.restore();
  return output;
}

function resolveReflectionBlurPx(reflectionCanvas: HTMLCanvasElement, blurAmount: number): number {
  const normalizedAmount = clamp(blurAmount, 0.1, 100) / 100;
  const maxBlurPx = Math.max(12, Math.min(reflectionCanvas.width, reflectionCanvas.height) * (150 / 3840));
  return normalizedAmount * maxBlurPx;
}

function blendReflectionBlurByMask(
  lessBlurredLayer: HTMLCanvasElement,
  moreBlurredLayer: HTMLCanvasElement,
  blurMask: CanvasImageSource,
  cache?: StaticRenderCache,
  visibilityMask?: CanvasImageSource | null
): HTMLCanvasElement {
  const width = lessBlurredLayer.width;
  const height = lessBlurredLayer.height;

  const lessBlurredCtx = lessBlurredLayer.getContext('2d');
  const moreBlurredCtx = moreBlurredLayer.getContext('2d');
  if (!lessBlurredCtx || !moreBlurredCtx) {
    return moreBlurredLayer;
  }
  // Both blur layers retain the original masked alpha silhouette. Pixels
  // outside its mask cannot contribute, even in the later transition blur.
  const region = cache && visibilityMask
    ? cache.mask(visibilityMask, width, height)
    : { x: 0, y: 0, width, height };
  if (!region.width || !region.height) return createCanvas(width, height);
  let maskPixels: Uint8ClampedArray;
  if (cache) {
    maskPixels = cache.pixels(blurMask, width, height, region);
  } else {
    const maskCanvas = createCanvas(width, height);
    const maskCtx = maskCanvas.getContext('2d');
    if (!maskCtx) return moreBlurredLayer;
    maskCtx.drawImage(blurMask, 0, 0, width, height);
    maskPixels = maskCtx.getImageData(0, 0, width, height).data;
    releaseScopedCanvas(maskCanvas);
  }
  const lessBlurredData = lessBlurredCtx.getImageData(region.x, region.y, region.width, region.height);
  const moreBlurredData = moreBlurredCtx.getImageData(region.x, region.y, region.width, region.height);
  releaseScopedCanvas(lessBlurredLayer);
  releaseScopedCanvas(moreBlurredLayer);
  const output = createCanvas(width, height);
  const outputCtx = output.getContext('2d');
  if (!outputCtx) {
    if (isCanvasMemoryScopeActive()) throw new Error('Not enough canvas memory for reflection export');
    return moreBlurredLayer;
  }
  const outputData = outputCtx.createImageData(region.width, region.height);

  for (let index = 0; index < outputData.data.length; index += 4) {
    const maskAlpha = maskPixels[index + 3] / 255;
    const maskLuminance =
      (maskPixels[index] + maskPixels[index + 1] + maskPixels[index + 2]) / (255 * 3);
    const normalizedMix = clamp(maskLuminance * maskAlpha, 0, 1);
    const mix = normalizedMix * normalizedMix * (3 - 2 * normalizedMix);

    outputData.data[index] = Math.round(
      lessBlurredData.data[index] * (1 - mix) + moreBlurredData.data[index] * mix
    );
    outputData.data[index + 1] = Math.round(
      lessBlurredData.data[index + 1] * (1 - mix) + moreBlurredData.data[index + 1] * mix
    );
    outputData.data[index + 2] = Math.round(
      lessBlurredData.data[index + 2] * (1 - mix) + moreBlurredData.data[index + 2] * mix
    );
    outputData.data[index + 3] = Math.round(
      lessBlurredData.data[index + 3] * (1 - mix) + moreBlurredData.data[index + 3] * mix
    );
  }

  outputCtx.putImageData(outputData, region.x, region.y);
  return output;
}

function cloneCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
  const clone = createCanvas(source.width, source.height);
  const ctx = clone.getContext('2d');
  if (!ctx) {
    return clone;
  }
  ctx.drawImage(source, 0, 0);
  return clone;
}

function getSourceSize(source: CanvasImageSource): { width: number; height: number } {
  if (source instanceof HTMLVideoElement) {
    return {
      width: Math.max(1, source.videoWidth || source.width || 1),
      height: Math.max(1, source.videoHeight || source.height || 1)
    };
  }

  if (source instanceof HTMLImageElement) {
    return {
      width: Math.max(1, source.naturalWidth || source.width || 1),
      height: Math.max(1, source.naturalHeight || source.height || 1)
    };
  }

  const canvasLike = source as HTMLCanvasElement | ImageBitmap | OffscreenCanvas | VideoFrame;
  const width = 'displayWidth' in canvasLike ? canvasLike.displayWidth : canvasLike.width;
  const height = 'displayHeight' in canvasLike ? canvasLike.displayHeight : canvasLike.height;

  return {
    width: Math.max(1, Number(width) || 1),
    height: Math.max(1, Number(height) || 1)
  };
}

function drawDiffuseLayer(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  mask: CanvasImageSource,
  size: number,
  adjustments?: DiffuseAdjustments
): void {
  ctx.drawImage(image, 0, 0, size, size);

  if (!adjustments || isNeutralDiffuseAdjustments(adjustments)) {
    return;
  }

  const adjustedCanvas = createCanvas(size, size);
  const adjustedCtx = adjustedCanvas.getContext('2d');
  if (!adjustedCtx) {
    return;
  }

  drawImageWithDiffuseAdjustments(adjustedCtx, image, adjustments, null, {
    dx: 0,
    dy: 0,
    dw: size,
    dh: size
  });

  const outsideMaskCanvas = createOutsideScreenMask(mask, size);
  adjustedCtx.save();
  adjustedCtx.globalCompositeOperation = 'destination-in';
  adjustedCtx.drawImage(outsideMaskCanvas, 0, 0, size, size);
  adjustedCtx.restore();

  ctx.drawImage(adjustedCanvas, 0, 0, size, size);
  releaseScopedCanvas(outsideMaskCanvas);
  releaseScopedCanvas(adjustedCanvas);
}

/**
 * Draws a prepared layer over the diffuse one, clipped to the same region: the
 * area outside the screen cut-out. Reusing the diffuse layer's own mask is what
 * keeps a recoloured region from spilling across the screen.
 */
function drawDiffuseOverlayLayer(
  ctx: CanvasRenderingContext2D,
  overlay: CanvasImageSource,
  mask: CanvasImageSource,
  size: number
): void {
  const clippedCanvas = createCanvas(size, size);
  const clippedCtx = clippedCanvas.getContext('2d');
  if (!clippedCtx) {
    return;
  }

  clippedCtx.drawImage(overlay, 0, 0, size, size);
  clippedCtx.save();
  clippedCtx.globalCompositeOperation = 'destination-in';
  const outside = createOutsideScreenMask(mask, size);
  clippedCtx.drawImage(outside, 0, 0, size, size);
  clippedCtx.restore();

  ctx.drawImage(clippedCanvas, 0, 0, size, size);
  releaseScopedCanvas(outside);
  releaseScopedCanvas(clippedCanvas);
}

export function drawImageWithDiffuseAdjustments(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  adjustments: DiffuseAdjustments | undefined,
  sourceRect: ImageSourceRect | null,
  destRect: ImageDestRect
): void {
  if (!adjustments || isNeutralDiffuseAdjustments(adjustments)) {
    drawImageRect(ctx, image, sourceRect, destRect);
    return;
  }

  if (shouldUseCanvasDiffuseFilter()) {
    ctx.save();
    ctx.filter = createDiffuseFilter(adjustments);
    drawImageRect(ctx, image, sourceRect, destRect);
    ctx.restore();
    return;
  }

  const patchWidth = Math.max(1, Math.round(destRect.dw));
  const patchHeight = Math.max(1, Math.round(destRect.dh));
  const patchCanvas = createCanvas(patchWidth, patchHeight);
  const patchCtx = patchCanvas.getContext('2d');
  if (!patchCtx) {
    drawImageRect(ctx, image, sourceRect, destRect);
    return;
  }

  patchCtx.imageSmoothingEnabled = ctx.imageSmoothingEnabled;
  patchCtx.imageSmoothingQuality = ctx.imageSmoothingQuality;
  drawImageRect(patchCtx, image, sourceRect, {
    dx: 0,
    dy: 0,
    dw: patchWidth,
    dh: patchHeight
  });

  if (!applyDiffusePixelAdjustments(patchCtx, patchWidth, patchHeight, adjustments)) {
    drawImageRect(ctx, image, sourceRect, destRect);
    return;
  }

  ctx.drawImage(patchCanvas, destRect.dx, destRect.dy, destRect.dw, destRect.dh);
}

function applyBackgroundTreatment(
  targetCanvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  backgroundMask: CanvasImageSource | null,
  size: number,
  adjustments?: BackgroundAdjustments,
  cache?: StaticRenderCache
): void {
  if (!backgroundMask || !adjustments || isNeutralBackgroundAdjustments(adjustments)) {
    return;
  }

  const maskCanvas = cache
    ? cache.canvas('background-coverage', [backgroundMask, size], () => createMaskCoverageCanvas(backgroundMask, size))
    : createMaskCoverageCanvas(backgroundMask, size);

  if (adjustments.transparent) {
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.drawImage(maskCanvas, 0, 0, size, size);
    ctx.restore();
    releaseScopedCanvas(maskCanvas);
    return;
  }

  const sourceCanvas = createCanvas(size, size);
  const sourceCtx = sourceCanvas.getContext('2d');
  const maskCtx = maskCanvas.getContext('2d');
  if (!sourceCtx || !maskCtx) {
    return;
  }

  sourceCtx.drawImage(targetCanvas, 0, 0);
  const sourceData = sourceCtx.getImageData(0, 0, size, size);
  const maskData = maskCtx.getImageData(0, 0, size, size);
  releaseScopedCanvas(maskCanvas);
  releaseScopedCanvas(sourceCanvas);

  const recoloredCanvas = createCanvas(size, size);
  const recoloredCtx = recoloredCanvas.getContext('2d');
  if (!recoloredCtx) {
    return;
  }

  const recolored = recoloredCtx.createImageData(size, size);
  const sourcePixels = sourceData.data;
  const maskPixels = maskData.data;
  const outPixels = recolored.data;

  const dark = parseHexColor(adjustments.shadowColor);
  const mid = parseHexColor(adjustments.midColor);
  const light = parseHexColor(adjustments.highlightColor);

  for (let index = 0; index < outPixels.length; index += 4) {
    const maskCoverage = maskPixels[index + 3] / 255;
    if (maskCoverage <= 0) {
      continue;
    }

    const sourceAlpha = sourcePixels[index + 3] / 255;
    if (sourceAlpha <= 0) {
      continue;
    }

    const luminance = getPerceivedLuminance(sourcePixels[index], sourcePixels[index + 1], sourcePixels[index + 2]);
    const mappedColor =
      adjustments.mode === 'tritone'
        ? sampleTritoneColor(dark, mid, light, luminance)
        : lerpColor(dark, light, luminance);

    outPixels[index] = mappedColor.r;
    outPixels[index + 1] = mappedColor.g;
    outPixels[index + 2] = mappedColor.b;
    outPixels[index + 3] = Math.round(sourceAlpha * maskCoverage * 255);
  }

  recoloredCtx.putImageData(recolored, 0, 0);
  ctx.drawImage(recoloredCanvas, 0, 0);
  releaseScopedCanvas(maskCanvas);
  releaseScopedCanvas(sourceCanvas);
  releaseScopedCanvas(recoloredCanvas);
}

function createDiffuseFilter(adjustments: DiffuseAdjustments): string {
  return [
    `hue-rotate(${adjustments.hue}deg)`,
    `saturate(${adjustments.saturation}%)`,
    `brightness(${adjustments.brightness}%)`,
    `contrast(${adjustments.contrast}%)`,
    adjustments.monochrome ? 'grayscale(100%)' : 'grayscale(0%)'
  ].join(' ');
}

let canvasDiffuseFilterSupported: boolean | null = null;

function shouldUseCanvasDiffuseFilter(): boolean {
  if (isSafariCanvasRuntime() || isWindowsChromiumCanvasRuntime()) {
    return false;
  }

  return supportsCanvasDiffuseFilter();
}

function isSafariCanvasRuntime(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  const vendor = navigator.vendor || '';
  const isAppleSafari = /Safari/i.test(userAgent) && /Apple/i.test(vendor);
  const isChromiumOrFirefoxShell = /Chrome|CriOS|Chromium|Edg|OPR|FxiOS|Android/i.test(userAgent);
  return isAppleSafari && !isChromiumOrFirefoxShell;
}

function isWindowsChromiumCanvasRuntime(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const isWindows = /Win/i.test(platform) || /Windows/i.test(userAgent);
  const isChromiumShell = /Chrome|Chromium|CriOS|Edg|OPR/i.test(userAgent);
  const isFirefoxShell = /Firefox|FxiOS/i.test(userAgent);
  return isWindows && isChromiumShell && !isFirefoxShell;
}

function isMobileCanvasRuntime(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  return /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent);
}

function supportsCanvasDiffuseFilter(): boolean {
  if (canvasDiffuseFilterSupported !== null) {
    return canvasDiffuseFilterSupported;
  }

  const sourceCanvas = createCanvas(1, 1);
  const sourceCtx = sourceCanvas.getContext('2d');
  const targetCanvas = createCanvas(1, 1);
  const targetCtx = targetCanvas.getContext('2d');
  if (!sourceCtx || !targetCtx || !('filter' in targetCtx)) {
    canvasDiffuseFilterSupported = false;
    return canvasDiffuseFilterSupported;
  }

  try {
    sourceCtx.fillStyle = 'rgb(255, 0, 0)';
    sourceCtx.fillRect(0, 0, 1, 1);
    targetCtx.filter = 'hue-rotate(120deg) saturate(130%) brightness(90%) contrast(110%)';
    targetCtx.drawImage(sourceCanvas, 0, 0);
    targetCtx.filter = 'none';
    const pixel = targetCtx.getImageData(0, 0, 1, 1).data;
    canvasDiffuseFilterSupported = !(pixel[0] > 240 && pixel[1] < 10 && pixel[2] < 10);
  } catch {
    canvasDiffuseFilterSupported = false;
  }

  return canvasDiffuseFilterSupported;
}

function drawImageRect(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  sourceRect: ImageSourceRect | null,
  destRect: ImageDestRect
): void {
  if (sourceRect) {
    ctx.drawImage(
      image,
      sourceRect.sx,
      sourceRect.sy,
      sourceRect.sw,
      sourceRect.sh,
      destRect.dx,
      destRect.dy,
      destRect.dw,
      destRect.dh
    );
    return;
  }

  ctx.drawImage(image, destRect.dx, destRect.dy, destRect.dw, destRect.dh);
}

function applyDiffusePixelAdjustments(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  adjustments: DiffuseAdjustments
): boolean {
  try {
    const imageData = ctx.getImageData(0, 0, width, height);
    const pixels = imageData.data;
    const hueRadians = (adjustments.hue * Math.PI) / 180;
    const hueCos = Math.cos(hueRadians);
    const hueSin = Math.sin(hueRadians);
    const saturation = Math.max(0, adjustments.saturation / 100);
    const brightness = Math.max(0, adjustments.brightness / 100);
    const contrast = Math.max(0, adjustments.contrast / 100);

    for (let index = 0; index < pixels.length; index += 4) {
      if (pixels[index + 3] === 0) {
        continue;
      }

      let r = pixels[index];
      let g = pixels[index + 1];
      let b = pixels[index + 2];

      if (adjustments.hue !== 0) {
        [r, g, b] = applyHueRotate(r, g, b, hueCos, hueSin);
      }

      if (saturation !== 1) {
        [r, g, b] = applySaturate(r, g, b, saturation);
      }

      if (brightness !== 1) {
        r *= brightness;
        g *= brightness;
        b *= brightness;
      }

      if (contrast !== 1) {
        r = (r - 128) * contrast + 128;
        g = (g - 128) * contrast + 128;
        b = (b - 128) * contrast + 128;
      }

      if (adjustments.monochrome) {
        [r, g, b] = applySaturate(r, g, b, 0);
      }

      pixels[index] = clampByte(r);
      pixels[index + 1] = clampByte(g);
      pixels[index + 2] = clampByte(b);
    }

    ctx.putImageData(imageData, 0, 0);
    return true;
  } catch {
    return false;
  }
}

function applyHueRotate(
  r: number,
  g: number,
  b: number,
  cos: number,
  sin: number
): [number, number, number] {
  return [
    (0.213 + cos * 0.787 - sin * 0.213) * r +
      (0.715 - cos * 0.715 - sin * 0.715) * g +
      (0.072 - cos * 0.072 + sin * 0.928) * b,
    (0.213 - cos * 0.213 + sin * 0.143) * r +
      (0.715 + cos * 0.285 + sin * 0.14) * g +
      (0.072 - cos * 0.072 - sin * 0.283) * b,
    (0.213 - cos * 0.213 - sin * 0.787) * r +
      (0.715 - cos * 0.715 + sin * 0.715) * g +
      (0.072 + cos * 0.928 + sin * 0.072) * b
  ];
}

function applySaturate(r: number, g: number, b: number, saturation: number): [number, number, number] {
  return [
    (0.213 + 0.787 * saturation) * r + (0.715 - 0.715 * saturation) * g + (0.072 - 0.072 * saturation) * b,
    (0.213 - 0.213 * saturation) * r + (0.715 + 0.285 * saturation) * g + (0.072 - 0.072 * saturation) * b,
    (0.213 - 0.213 * saturation) * r + (0.715 - 0.715 * saturation) * g + (0.072 + 0.928 * saturation) * b
  ];
}

function clampByte(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function isNeutralDiffuseAdjustments(adjustments: DiffuseAdjustments): boolean {
  return (
    adjustments.hue === 0 &&
    adjustments.saturation === 100 &&
    adjustments.brightness === 100 &&
    adjustments.contrast === 100 &&
    !adjustments.monochrome
  );
}

function isNeutralBackgroundAdjustments(adjustments: BackgroundAdjustments): boolean {
  return !adjustments.transparent && adjustments.mode === 'original';
}

function createOutsideScreenMask(mask: CanvasImageSource, size: number): HTMLCanvasElement {
  const maskCanvas = createCanvas(size, size);
  const maskCtx = maskCanvas.getContext('2d');
  if (!maskCtx) {
    return maskCanvas;
  }

  maskCtx.clearRect(0, 0, size, size);
  maskCtx.drawImage(mask, 0, 0, size, size);

  const imageData = maskCtx.getImageData(0, 0, size, size);
  const pixels = imageData.data;

  for (let index = 0; index < pixels.length; index += 4) {
    const r = pixels[index];
    const g = pixels[index + 1];
    const b = pixels[index + 2];
    const a = pixels[index + 3] / 255;
    const luminance = ((r + g + b) / (255 * 3)) * a;
    const outsideAlpha = Math.round((1 - luminance) * 255);

    pixels[index] = 255;
    pixels[index + 1] = 255;
    pixels[index + 2] = 255;
    pixels[index + 3] = outsideAlpha;
  }

  maskCtx.putImageData(imageData, 0, 0);
  return maskCanvas;
}

function createMaskCoverageCanvas(mask: CanvasImageSource, size: number): HTMLCanvasElement {
  const maskCanvas = createCanvas(size, size);
  const maskCtx = maskCanvas.getContext('2d');
  if (!maskCtx) {
    return maskCanvas;
  }

  maskCtx.clearRect(0, 0, size, size);
  maskCtx.drawImage(mask, 0, 0, size, size);

  const imageData = maskCtx.getImageData(0, 0, size, size);
  const pixels = imageData.data;

  for (let index = 0; index < pixels.length; index += 4) {
    const luminance = getPerceivedLuminance(pixels[index], pixels[index + 1], pixels[index + 2]);
    // Background mask semantics: white keeps foreground objects, dark marks removable/recolorable background.
    const alpha = Math.round((1 - luminance) * (pixels[index + 3] / 255) * 255);
    pixels[index] = 255;
    pixels[index + 1] = 255;
    pixels[index + 2] = 255;
    pixels[index + 3] = alpha;
  }

  maskCtx.putImageData(imageData, 0, 0);
  return maskCanvas;
}

function createCustomPlacementSource(
  image: CanvasImageSource,
  targetAspect: number,
  placement?: CustomPlacement | null
): HTMLCanvasElement {
  const { width: imageWidth, height: imageHeight } = getSourceSize(image);
  const naturalBase = Math.max(imageWidth, imageHeight, 1024);
  // Mobile only. Safari will not create a canvas above 16.7 Mpx (4096x4096),
  // and on a phone this surface is the one a 48 Mpx photo would blow up. Any
  // cap changes how a large source is resampled, so desktop keeps the full
  // size and exports exactly as before; measured, no cap was the only setting
  // that reproduced the previous output.
  const base = isCanvasMemoryScopeActive() && isMobileCanvasRuntime()
    ? Math.min(naturalBase, 4096)
    : naturalBase;

  let width: number;
  let height: number;
  if (targetAspect >= 1) {
    width = base;
    height = Math.max(1, Math.round(base / targetAspect));
  } else {
    height = base;
    width = Math.max(1, Math.round(base * targetAspect));
  }

  const canvas = createCanvas(width, height);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('2D context is not available for custom placement canvas');
  }

  ctx.clearRect(0, 0, width, height);

  const coverScale = Math.max(width / imageWidth, height / imageHeight);
  const normalizedPlacement = normalizeCustomPlacement({
    scale: placement?.scale ?? 1,
    offsetX: placement?.offsetX ?? 0,
    offsetY: placement?.offsetY ?? 0
  });

  const drawWidth = imageWidth * coverScale * normalizedPlacement.scale;
  const drawHeight = imageHeight * coverScale * normalizedPlacement.scale;
  const centerX = width / 2 + normalizedPlacement.offsetX * width;
  const centerY = height / 2 + normalizedPlacement.offsetY * height;
  const dx = centerX - drawWidth / 2;
  const dy = centerY - drawHeight / 2;

  ctx.drawImage(image, dx, dy, drawWidth, drawHeight);
  return canvas;
}

function normalizeCustomPlacement(
  placement: CustomPlacement
): CustomPlacement {
  const scale = clamp(Number.isFinite(placement.scale) ? placement.scale : 1, 0.05, 6);
  const offsetX = Number.isFinite(placement.offsetX) ? placement.offsetX : 0;
  const offsetY = Number.isFinite(placement.offsetY) ? placement.offsetY : 0;
  return { scale, offsetX, offsetY };
}

function getQuadAspect(quad: Point[]): number {
  const top = edgeLength(quad[0], quad[1]);
  const bottom = edgeLength(quad[3], quad[2]);
  const left = edgeLength(quad[0], quad[3]);
  const right = edgeLength(quad[1], quad[2]);
  const width = (top + bottom) / 2;
  const height = (left + right) / 2;
  const aspect = width / Math.max(1e-6, height);

  if (!Number.isFinite(aspect) || aspect <= 0) {
    return 1;
  }

  return Math.max(0.05, Math.min(20, aspect));
}

function edgeLength(a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.hypot(dx, dy);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function createTransparencyMask(
  baseImage: CanvasImageSource,
  screenMaskImage: CanvasImageSource,
  size: number
): HTMLCanvasElement {
  const baseCanvas = createCanvas(size, size);
  const baseCtx = baseCanvas.getContext('2d');
  if (!baseCtx) {
    return baseCanvas;
  }
  baseCtx.clearRect(0, 0, size, size);
  baseCtx.drawImage(baseImage, 0, 0, size, size);
  const baseData = baseCtx.getImageData(0, 0, size, size);

  const maskCanvas = createCanvas(size, size);
  const maskCtx = maskCanvas.getContext('2d');
  if (!maskCtx) {
    return baseCanvas;
  }
  maskCtx.clearRect(0, 0, size, size);
  maskCtx.drawImage(screenMaskImage, 0, 0, size, size);
  const screenMaskData = maskCtx.getImageData(0, 0, size, size);
  releaseScopedCanvas(maskCanvas);

  const output = isCanvasMemoryScopeActive() ? baseCanvas : createCanvas(size, size);
  const outputCtx = output.getContext('2d');
  if (!outputCtx) {
    return baseCanvas;
  }

  const out = isCanvasMemoryScopeActive() ? baseData : outputCtx.createImageData(size, size);
  const pixels = out.data;

  for (let index = 0; index < pixels.length; index += 4) {
    const baseAlpha = baseData.data[index + 3];

    const maskR = screenMaskData.data[index];
    const maskG = screenMaskData.data[index + 1];
    const maskB = screenMaskData.data[index + 2];
    const maskA = screenMaskData.data[index + 3] / 255;
    const screenAlpha = Math.round(((maskR + maskG + maskB) / (255 * 3)) * maskA * 255);

    const alpha = Math.max(baseAlpha, screenAlpha);
    pixels[index] = 255;
    pixels[index + 1] = 255;
    pixels[index + 2] = 255;
    pixels[index + 3] = alpha;
  }

  outputCtx.putImageData(out, 0, 0);
  return output;
}

function healTransparentSeams(canvas: HTMLCanvasElement, radius: number): void {
  const ctx = canvas.getContext('2d');
  if (!ctx || radius <= 0) {
    return;
  }

  const copy = createCanvas(canvas.width, canvas.height);
  const copyCtx = copy.getContext('2d');
  if (!copyCtx) {
    return;
  }
  copyCtx.drawImage(canvas, 0, 0);

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(copy, 0, 0);

  ctx.save();
  ctx.globalCompositeOperation = 'destination-over';
  for (let dy = -radius; dy <= radius; dy += 1) {
    for (let dx = -radius; dx <= radius; dx += 1) {
      if (dx === 0 && dy === 0) {
        continue;
      }
      ctx.drawImage(copy, dx, dy);
    }
  }
  ctx.restore();
}

function scaleQuad(
  quad: MockupTemplate['screenQuad'],
  targetSize: number,
  sourceSize: number
): MockupTemplate['screenQuad'] {
  if (sourceSize === targetSize) {
    return quad;
  }

  const factor = targetSize / sourceSize;
  return quad.map((point) => ({
    // Preserve subpixel precision when rendering/exporting to a different size.
    // Rounding here causes visible stepping in motion warp at 1920 -> 1080.
    x: point.x * factor,
    y: point.y * factor
  }));
}

export function parseHexColor(value: string): { r: number; g: number; b: number } {
  const normalized = value.trim().replace('#', '');
  const hex = normalized.length === 3
    ? normalized
        .split('')
        .map((char) => char + char)
        .join('')
    : normalized.padEnd(6, '0').slice(0, 6);

  const parsed = Number.parseInt(hex, 16);
  if (!Number.isFinite(parsed)) {
    return { r: 255, g: 255, b: 255 };
  }

  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255
  };
}

function lerpColor(
  from: { r: number; g: number; b: number },
  to: { r: number; g: number; b: number },
  amount: number
): { r: number; g: number; b: number } {
  const t = clamp01(amount);
  return {
    r: Math.round(from.r + (to.r - from.r) * t),
    g: Math.round(from.g + (to.g - from.g) * t),
    b: Math.round(from.b + (to.b - from.b) * t)
  };
}

export function sampleTritoneColor(
  shadow: { r: number; g: number; b: number },
  mid: { r: number; g: number; b: number },
  highlight: { r: number; g: number; b: number },
  amount: number
): { r: number; g: number; b: number } {
  const t = clamp01(amount);
  if (t <= 0.5) {
    return lerpColor(shadow, mid, t / 0.5);
  }
  return lerpColor(mid, highlight, (t - 0.5) / 0.5);
}

export function getPerceivedLuminance(r: number, g: number, b: number): number {
  return clamp01((0.2126 * r + 0.7152 * g + 0.0722 * b) / 255);
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}
